import { Link } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { PublicFooter } from '@/components/PublicFooter';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Terms() {
  usePageMeta({
    title: 'Terms of Service',
    description:
      'For Visibility Terms of Service — the agreement governing use of the reseller platform.',
    canonicalPath: '/legal/terms',
    ogPage: 'terms',
  });
  return (
    <div className="min-h-screen bg-fos-bg-base">
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1 text-sm text-fos-text-muted transition-colors hover:text-fos-text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <article className="prose prose-gray max-w-none">
          <h1>Terms of Service</h1>
          <p className="lead">Last updated: May 12, 2026</p>

          <p>
            Welcome to For Visibility. By using our service, you agree to these terms. Please read
            them carefully.
          </p>

          <h2>1. Service Description</h2>
          <p>
            For Visibility provides an AI-powered operating system for resellers, including listing
            management, inventory tracking, pricing intelligence, analytics, and related services
            (the "Service"). The Service is provided as a software-as-a-service (SaaS) subscription
            and is accessed via web browser.
          </p>

          <h2>2. Eligibility</h2>
          <p>
            You must be at least 18 years old to use the Service. By creating an account, you
            represent that you are 18 or older and that the information you provide is accurate and
            complete. If you are using the Service on behalf of a business, you represent that you
            have the authority to bind that business to these terms.
          </p>

          <h2>3. Account Registration & Security</h2>
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and
            for all activity that occurs under your account. You agree to notify us immediately of
            any unauthorized use of your account. We are not liable for any loss or damage arising
            from your failure to safeguard your login information.
          </p>

          <h2>4. Subscriptions & Billing</h2>
          <p>
            For Visibility offers tiered subscription plans (Free, Pro, Power, Studio). Paid plans
            are billed monthly or annually as selected during checkout. All fees are non-refundable
            except as expressly stated in our refund policy. We reserve the right to change our
            pricing with 30 days' notice — we will notify you by email before any price change takes
            effect.
          </p>

          <h3>4.1 Automatic Renewal</h3>
          <p>
            Subscriptions automatically renew at the end of each billing period unless you cancel
            before the renewal date. You can cancel at any time from your account settings.
            Cancellation takes effect at the end of your current billing period — you retain full
            access until then.
          </p>

          <h3>4.2 Data Export on Cancellation</h3>
          <p>
            When you cancel, you may export all your data in CSV and JSON formats. Your data remains
            available for export for 30 days after cancellation. After 30 days, your data is
            permanently deleted from our systems. We encourage all users to export before
            cancelling.
          </p>

          <h2>5. Usage Guidelines</h2>
          <p>
            You agree not to use the Service for any unlawful purpose or in violation of any
            applicable law or regulation. Specifically, you may not:
          </p>
          <ul>
            <li>Use the Service to list prohibited or illegal items</li>
            <li>Attempt to bypass rate limits, access controls, or billing</li>
            <li>Scrape, copy, or reproduce the Service for competitive use</li>
            <li>Use automated tools to interact with the Service beyond our public API terms</li>
            <li>Upload malware, phishing content, or otherwise compromise platform security</li>
          </ul>

          <h2>6. Intellectual Property</h2>
          <p>
            The Service, including its UI, AI models, algorithms, and branding, is the intellectual
            property of For Visibility and its licensors. You retain ownership of your data — your
            listings, inventory records, and transaction history are yours. We claim no ownership
            over the content you create using the Service.
          </p>

          <h2>7. AI & Automated Actions</h2>
          <p>
            The Service includes AI-powered features that can suggest pricing, optimize listings,
            and (on Power and Studio plans) take automated actions on your behalf. You are
            responsible for reviewing and approving AI actions before they are executed. We are not
            liable for losses resulting from AI-generated pricing errors, listing inaccuracies, or
            marketplace policy violations.
          </p>

          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, For Visibility and its officers, employees, and
            agents shall not be liable for any indirect, incidental, special, consequential, or
            punitive damages, including but not limited to loss of profits, data, or business
            opportunity, arising out of or related to your use of the Service. Our total liability
            for any claim arising from these terms or the Service shall not exceed the total amount
            you have paid us in the twelve months preceding the claim.
          </p>

          <h2>9. Export Controls</h2>
          <p>
            The Service may be subject to U.S. export control laws and regulations. You represent
            that you are not located in a country subject to a U.S. government embargo, and that you
            are not listed on any U.S. government list of prohibited or restricted parties. You
            agree to comply with all applicable export control laws and not to use the Service for
            any purpose prohibited by those laws.
          </p>

          <h2>10. Termination</h2>
          <p>
            We may suspend or terminate your access to the Service if you materially breach these
            terms and fail to cure the breach within 15 days of notice. We may also terminate your
            account if continued use would violate applicable law or cause harm to the platform or
            other users. Upon termination, your right to use the Service ceases immediately, and
            your data will be deleted per our data retention policy. Sections 6, 8, 9, and 11
            survive termination.
          </p>

          <h2>11. Changes to Terms</h2>
          <p>
            We may update these terms from time to time. Material changes will be communicated via
            email at least 30 days before they take effect. Continued use of the Service after
            changes become effective constitutes acceptance of the updated terms. If you do not
            agree to the changes, you may cancel your subscription before they take effect.
          </p>

          <h2>12. Governing Law</h2>
          <p>
            These terms are governed by the laws of the State of Delaware, without regard to its
            conflict of laws principles. Any disputes arising from these terms or the Service shall
            be resolved exclusively in the state or federal courts located in Delaware.
          </p>

          <h2>13. Contact</h2>
          <p>
            Questions about these terms? Contact us at{' '}
            <a href="mailto:legal@forvisibility.com">legal@forvisibility.com</a> or For Visibility
            Inc., 123 Commerce Street, Wilmington, DE 19801.
          </p>
        </article>
      </div>
      <PublicFooter />
    </div>
  );
}
